import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { IconButton } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { HeartOutlineIcon, HeartOutlineLargeIcon } from './icons';
import { useFavorites } from '../../utils/FavoritesContext';
import { useStyles } from './FavoriteButton.styles';
import clsx from 'clsx';
import CustomTooltip from './CustomTooltip';
import { FAVORITES } from '../../utils/constants';
import { FavoriteTooltip } from './FavoriteTooltip';
const TOOLTIP_PLACEMENT_MOBILE = 'left';
const TOOLTIP_PLACEMENT_DESKTOP = 'bottom';
const FavoriteButton = ({ productSku, productName, fromProductCard, showInitialTooltip, showFavoritedTooltip, onFavoriteToggle, onInitialTooltipClose, sendFavouritingProductAnalytics, isPDPRedesign = false, isNewCategoryDesign, }) => {
    const [localFavoritedTooltip, setLocalFavoritedTooltip] = useState(!!showFavoritedTooltip);
    const { classes } = useStyles();
    const { isFavorite, toggleFavorite } = useFavorites();
    const [screenSize, setScreenSize] = useState(null);
    const [showFloatingHeart, setShowFloatingHeart] = useState(false);
    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            if (width >= 1024) {
                setScreenSize('desktop');
            }
            else {
                setScreenSize('mobile');
            }
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);
    // screenSize is only set after mount; gating on it keeps the first client render identical to the SSG HTML
    // (localStorage favorites would otherwise cause an unpatched hydration mismatch in the redesign button).
    const isProductFavorite = screenSize !== null && productSku ? isFavorite(productSku) : false;
    useEffect(() => {
        setLocalFavoritedTooltip(!!showFavoritedTooltip);
    }, [showFavoritedTooltip]);
    const handleToggle = (event) => {
        setShowFloatingHeart(true);
        event.preventDefault();
        event.stopPropagation();
        toggleFavorite(productSku);
        if (!isProductFavorite) {
            sendFavouritingProductAnalytics === null || sendFavouritingProductAnalytics === void 0 ? void 0 : sendFavouritingProductAnalytics(productName);
        }
        setTimeout(() => setShowFloatingHeart(false), 1000);
        if (typeof onFavoriteToggle === 'function') {
            onFavoriteToggle();
        }
        if (typeof onInitialTooltipClose === 'function') {
            onInitialTooltipClose();
        }
        setTimeout(() => {
            setShowFloatingHeart(false);
        }, 1000);
    };
    const handleTooltipClose = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const renderIcon = () => {
        if (screenSize === null)
            return null;
        const isDesktop = screenSize === 'desktop';
        if (isProductFavorite && isDesktop && !fromProductCard) {
            return (_jsx(CustomTooltip, { title: FAVORITES.TOOLTIP_TITLE, placement: "bottom-end", offset: [81, -20], children: _jsx(FavoriteIcon, { className: `${classes.favoriteIcon} ${classes.favoriteIconLarge}` }) }));
        }
        if (isProductFavorite && localFavoritedTooltip) {
            return (_jsx(FavoriteTooltip, { open: localFavoritedTooltip, content: FAVORITES.FAVORITED_TOOLTIP_CONTENT, placement: isDesktop ? TOOLTIP_PLACEMENT_DESKTOP : TOOLTIP_PLACEMENT_MOBILE, isFavorited: true, onClose: (e) => {
                    handleTooltipClose(e);
                    setLocalFavoritedTooltip(false);
                }, children: _jsx(FavoriteIcon, { className: clsx(classes.favoriteIcon, fromProductCard && showFloatingHeart && classes.favoriteIconExpand) }) }));
        }
        if (isProductFavorite) {
            return (_jsx(FavoriteIcon, { className: clsx(classes.favoriteIcon, fromProductCard && showFloatingHeart && classes.favoriteIconExpand) }));
        }
        if (isDesktop && !fromProductCard) {
            return _jsx(HeartOutlineLargeIcon, {});
        }
        return (_jsx(FavoriteTooltip, { open: !!showInitialTooltip, content: FAVORITES.INITIAL_TOOLTIP_CONTENT, placement: isDesktop ? TOOLTIP_PLACEMENT_DESKTOP : TOOLTIP_PLACEMENT_MOBILE, onClose: (e) => {
                handleTooltipClose(e);
                if (typeof onInitialTooltipClose === 'function') {
                    onInitialTooltipClose();
                }
            }, children: _jsx("span", { children: _jsx(HeartOutlineIcon, {}) }) }));
    };
    const isDesktop = screenSize === 'desktop';
    const isLarge = isDesktop && !fromProductCard;
    const containerClass = `${classes.favoriteButtonContainer}${isLarge ? ' ' + classes.favoriteButtonContainerLarge : ''}`;
    const iconButtonClass = `${classes.favoriteButton}${isLarge ? ' ' + classes.favoriteButtonLarge : ''}`;
    // ── Redesign: simplified inline icon (no tooltips, no large variant) ──────
    if (isPDPRedesign) {
        const heartOutlineIconProps = isNewCategoryDesign ? { height: '32', width: '32' } : { viewBox: '0 0 24 24' };
        return (_jsx("div", { className: classes.favoriteButtonContainerRedesign, children: _jsx(IconButton, { className: classes.favoriteButtonRedesign, onClick: handleToggle, "aria-pressed": isProductFavorite, "aria-label": isProductFavorite ? FAVORITES.ARIA_LABEL_REMOVE : FAVORITES.ARIA_LABEL_ADD, type: "button", children: isProductFavorite ? (_jsx(FavoriteIcon, { className: clsx(classes.favoriteIconRedesign, classes.favoriteIconRedesignActive, isNewCategoryDesign && classes.favoriteIconRedesignNewCategory, showFloatingHeart && classes.favoriteIconExpand) })) : (_jsx(HeartOutlineIcon, Object.assign({}, heartOutlineIconProps))) }) }));
    }
    const renderFloatingIcon = () => {
        const isDesktop = screenSize === 'desktop';
        if (isProductFavorite) {
            if (isDesktop && !fromProductCard) {
                return _jsx(FavoriteIcon, { className: clsx(classes.favoriteIcon, classes.favoriteIconLarge) });
            }
            return (_jsx(FavoriteIcon, { className: clsx(classes.favoriteIcon, classes.floatingHeart, !fromProductCard && !isDesktop && classes.fromProductCardMobile) }));
        }
    };
    return (_jsxs(_Fragment, { children: [showFloatingHeart && fromProductCard && renderFloatingIcon(), _jsx("div", { className: containerClass, onClick: handleToggle, role: "button", tabIndex: 0, "aria-pressed": isProductFavorite, "aria-label": isProductFavorite ? FAVORITES.ARIA_LABEL_REMOVE : FAVORITES.ARIA_LABEL_ADD, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(e);
                    }
                }, children: renderIcon() && (_jsx(IconButton, { className: iconButtonClass, tabIndex: -1, "aria-hidden": "true", children: renderIcon() })) })] }));
};
export default FavoriteButton;
